import {
  ref,
  reactive,
  Ref,
  computed,
} from 'vue'
import { defineStore } from 'pinia'
import scrollLock from 'src/utils/scrollLock'
import { Laboratory } from 'www/vue_components/common/Analyses/types'
import { getLaboratoriesList } from 'www/vue_components/common/Analyses/api'
import { handleRequestError } from 'www/vue_components/common/Analyses/functions/handleRequestError'
import { RequestLabList, LabPaginator } from 'www/vue_components/common/Analyses/components/LaboratoriesList/types'

const paginatorDefault = {
  next: 1,
  total: 1,
  current: 1,
}

const useLabsStore = defineStore('labsStore', () => {
  const isRequesting: Ref<boolean> = ref(false)
  const isActiveDrawer: Ref<boolean> = ref(false)
  const isWithoutRequests: Ref<boolean> = ref(true)
  const laboratoryList: Ref<Array<{ id: number, lpu: Laboratory }>> = ref([])
  const laboratory: Partial<Laboratory> = reactive({})
  const requestLabData: Omit<RequestLabList, 'page'> = reactive({
    partnerSlug: '',
    townTranslit: '',
    searchString: '',
    hasCartAnalyzes: false,
    categorySlug: '',
  })
  const labPaginator: LabPaginator = reactive({ ...paginatorDefault })

  const labIdParam = computed(() => (laboratory?.id ? `?lpu_id=${laboratory.id}` : ''))

  async function showDrawer() {
    isActiveDrawer.value = true
    scrollLock.disableScroll()
    await requestLabListLazyLoad()
  }

  function hideDrawer() {
    isActiveDrawer.value = false
    scrollLock.enableScroll()

    resetLabPaginator()
    updateLabList([])
    updateIsWithoutRequests(true)
  }

  function updateIsRequesting(payload: boolean) {
    isRequesting.value = payload
  }

  function updateIsWithoutRequests(payload: boolean) {
    isWithoutRequests.value = payload
  }

  function updateLabList(payload: Array<{ id: number, lpu: Laboratory }>) {
    laboratoryList.value = payload
  }

  function resetLabPaginator() {
    Object.assign(labPaginator, { ...paginatorDefault })
  }

  function updateLabPaginator(payload: Partial<LabPaginator>) {
    Object.assign(labPaginator, payload)
  }

  function updateRequestLabData(payload: Partial<RequestLabList>) {
    Object.assign(requestLabData, payload)
  }

  function updateLaboratory(payload: Laboratory) {
    Object.assign(laboratory, payload)
  }

  async function requestLabListSearch() {
    try {
      if (isRequesting.value) {
        return
      }

      updateIsRequesting(true)

      const { data } = await getLaboratoriesList({
        partnerSlug: requestLabData.partnerSlug,
        townTranslit: requestLabData.townTranslit,
        searchString: requestLabData.searchString,
        hasCartAnalyzes: requestLabData.hasCartAnalyzes,
        categorySlug: requestLabData.categorySlug,
      })

      updateIsWithoutRequests(false)
      updateIsRequesting(false)
      updateLabList(data.laboratories)
      resetLabPaginator()
      updateLabPaginator({
        total: data.total_pages_number,
      })
    } catch (error: any) {
      updateIsRequesting(false)
      handleRequestError({ error, description: 'Ошибка запроса списка лабораторий' }, '#use-vue-base-dialog')
    }
  }

  async function resetLabList() {
    try {
      if (isRequesting.value) {
        return
      }

      updateIsRequesting(true)

      const { data } = await getLaboratoriesList({
        page: 1,
        partnerSlug: requestLabData.partnerSlug,
        townTranslit: requestLabData.townTranslit,
        searchString: '',
        hasCartAnalyzes: requestLabData.hasCartAnalyzes,
        categorySlug: requestLabData.categorySlug,
      })

      updateIsWithoutRequests(false)
      updateIsRequesting(false)
      updateLabList(data.laboratories)
      updateLabPaginator({
        total: data.total_pages_number,
      })
    } catch (error: any) {
      updateIsRequesting(false)
      handleRequestError({ error, description: 'Ошибка запроса списка лабораторий' }, '#use-vue-base-dialog')
    }
  }

  async function requestLabListLazyLoad() {
    try {
      if (isRequesting.value) {
        return
      }

      if (labPaginator.next > labPaginator.total || labPaginator.next <= 0) {
        return
      }

      updateIsRequesting(true)

      const { data } = await getLaboratoriesList({
        page: labPaginator.next,
        partnerSlug: requestLabData.partnerSlug,
        townTranslit: requestLabData.townTranslit,
        searchString: requestLabData.searchString,
        hasCartAnalyzes: requestLabData.hasCartAnalyzes,
        categorySlug: requestLabData.categorySlug,
      })

      updateIsWithoutRequests(false)
      updateIsRequesting(false)
      updateLabList([...laboratoryList.value, ...data.laboratories])
      updateLabPaginator({
        next: labPaginator.next < data.total_pages_number
          ? labPaginator.next + 1
          : data.total_pages_number,
        current: labPaginator.next,
        total: data.total_pages_number,
      })
    } catch (error: any) {
      updateIsRequesting(false)
      handleRequestError({ error, description: 'Ошибка запроса списка лабораторий' }, '#use-vue-base-dialog')
    }
  }

  return {
    isRequesting,
    labPaginator,
    isActiveDrawer,
    laboratoryList,
    requestLabData,
    isWithoutRequests,
    laboratory,
    labIdParam,
    resetLabList,
    showDrawer,
    hideDrawer,
    requestLabListLazyLoad,
    updateLaboratory,
    requestLabListSearch,
    resetLabPaginator,
    updateLabPaginator,
    updateLabList,
    updateRequestLabData,
  }
})

export default useLabsStore
